import { QuarterDataKey } from '../../quarter/services/QuarterService'
import { Quarter } from '../../quarter/types/Quarter'
import { TopGame } from '../../top-game/types/TopGame'
import { MarketShareSortColumnId } from '../types/MarketShareSortColumn'

type SortMetric = 'revenue' | 'downloads'

type Top500ValueKey = QuarterDataKey.RevenueTop500Value | QuarterDataKey.DownloadsTop500Value

/**
 * Maps the active sort column to the Top 500 metric it represents. Revenue columns
 * (and any unsupported / default sort) use the Top 500 Revenue metric; Downloads
 * columns use the Top 500 Downloads metric.
 */
const getSortMetric = (sortColumnId: MarketShareSortColumnId | undefined): SortMetric => {
  switch (sortColumnId) {
    case MarketShareSortColumnId.Q1Downloads:
    case MarketShareSortColumnId.Q1DownloadsChange:
    case MarketShareSortColumnId.Q2Downloads:
    case MarketShareSortColumnId.Q2DownloadsChange:
      return 'downloads'
    default:
      return 'revenue'
  }
}

/**
 * When the active sort column is a VALUE (non-change) metric column, returns the index
 * (0 or 1) of the selected quarter the table is being sorted by. Change columns and
 * unsupported sorts return null, indicating union behaviour across the selected quarters.
 */
const getValueSortQuarterIndex = (sortColumnId: MarketShareSortColumnId | undefined): 0 | 1 | null => {
  switch (sortColumnId) {
    case MarketShareSortColumnId.Q1Revenue:
    case MarketShareSortColumnId.Q1Downloads:
      return 0
    case MarketShareSortColumnId.Q2Revenue:
    case MarketShareSortColumnId.Q2Downloads:
      return 1
    default:
      return null
  }
}

const getTop500ValueKey = (metric: SortMetric): Top500ValueKey =>
  metric === 'downloads' ? QuarterDataKey.DownloadsTop500Value : QuarterDataKey.RevenueTop500Value

/**
 * A game belongs to the metric's Top 500 for a quarter when its recomputed Top 500 value
 * (`downloadsTop500Value` / `revenueTop500Value`) for that quarter is > 0. This is the SAME
 * value `QuarterService` uses to derive the displayed percentage, so membership, the
 * displayed `%` and the 0%-row filtering all stay consistent — a game in the list always has
 * a non-zero `%`, and a game with a 0% column is never shown. (GR-2878, GR-2860)
 */
const isInQuarterTop500 = (topGame: TopGame, quarter: Quarter | undefined, valueKey: Top500ValueKey): boolean => {
  if (!quarter) {
    return false
  }
  const gameQuarter = topGame.marketLevelQuarters?.[quarter.quarterIdentifier]
  return !!gameQuarter && gameQuarter[valueKey] > 0
}

/**
 * Builds a predicate that scopes the Top 500 Games table to the games belonging to the
 * Top 500 ranking matching the active sort column, using each game's recomputed
 * `downloadsTop500Value` / `revenueTop500Value` (the values that also drive the displayed `%`).
 *
 * Behaviour:
 * - Whole market (no scope filter) + a VALUE sort (Q1/Q2 Revenue or Downloads): keep only
 *   games in the SORTED quarter's Top 500 for that metric. The displayed list IS that
 *   quarter's actual Top 500, so it has no out-of-Top-500 (0%) entries. (GR-2878, GR-2860)
 * - Scope-filtered views (subgenre / genre / publisher) and Change columns: keep games in
 *   the Top 500 for the active metric in at least ONE selected quarter (union). This keeps
 *   the displayed set stable across both quarter columns so a scoped comparison and the
 *   Change columns still surface games that entered / left the Top 500. (GR-2511, GR-2476)
 */
export const buildTopGamesTop500Filter = ({
  selectedQuarters,
  sortColumnId,
  isScopeFiltered,
}: {
  selectedQuarters: Quarter[]
  sortColumnId: MarketShareSortColumnId | undefined
  isScopeFiltered: boolean
}): ((topGame: TopGame) => boolean) => {
  const metric = getSortMetric(sortColumnId)
  const valueKey = getTop500ValueKey(metric)
  const valueSortQuarterIndex = getValueSortQuarterIndex(sortColumnId)

  if (!isScopeFiltered && valueSortQuarterIndex !== null) {
    const sortedQuarter = selectedQuarters[valueSortQuarterIndex]
    if (!sortedQuarter) {
      return () => true
    }
    return (topGame: TopGame): boolean => isInQuarterTop500(topGame, sortedQuarter, valueKey)
  }

  return (topGame: TopGame): boolean => selectedQuarters.some((quarter) => isInQuarterTop500(topGame, quarter, valueKey))
}
